/**
 * Notifications Utility
 * Handles creation and management of in-app notifications
 */

import { collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc, doc, orderBy, limit, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { classifyTenantNotification } from './tenantNotificationBuckets';

/**
 * Create a notification
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.recipientUserId - User ID of recipient (optional if recipientEmailLower provided)
 * @param {string} notificationData.recipientEmailLower - Email of recipient (optional if recipientUserId provided)
 * @param {string} notificationData.type - Notification type
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {Object} notificationData.data - Additional data (agencyId, clientLinkId, etc.)
 * @returns {Promise<string>} Notification ID
 */
export async function createNotification(notificationData) {
  try {
    const {
      recipientUserId,
      recipientEmailLower,
      type,
      title,
      message,
      ...data
    } = notificationData;

    if (!recipientUserId && !recipientEmailLower) {
      throw new Error('Either recipientUserId or recipientEmailLower is required');
    }

    if (!type || !title || !message) {
      throw new Error('type, title, and message are required');
    }

    const notification = {
      recipientUserId: recipientUserId || null,
      recipientEmailLower: recipientEmailLower ? recipientEmailLower.toLowerCase().trim() : null,
      type,
      title,
      message,
      read: false,
      createdAt: serverTimestamp(),
      ...data
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export async function markNotificationAsRead(notificationId) {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

/**
 * Get unread notifications for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of notifications to return
 * @returns {Promise<Array>} Array of notifications
 */
/**
 * Mark all unread notifications for a user in a specific bucket as read.
 * Bucket must be one of: 'viewings', 'contracts', 'mijnPand'.
 * Optionally pass extraFilter to narrow down (e.g. { contractId: '...' }).
 */
export async function markNotificationsByBucketAsRead(userId, bucket, extraFilter = {}) {
  if (!userId || !bucket) return;
  try {
    const recipientQ = query(
      collection(db, 'notifications'),
      where('recipientUserId', '==', userId),
      where('read', '==', false)
    );
    const tenantQ = query(
      collection(db, 'notifications'),
      where('tenantId', '==', userId),
      where('read', '==', false)
    );

    const [recipientSnap, tenantSnap] = await Promise.all([
      getDocs(recipientQ).catch(() => ({ docs: [] })),
      getDocs(tenantQ).catch(() => ({ docs: [] })),
    ]);

    const seen = new Set();
    const allDocs = [...recipientSnap.docs, ...tenantSnap.docs].filter((d) => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });

    const matching = allDocs.filter((d) => {
      const data = d.data();
      if (classifyTenantNotification(data) !== bucket) return false;
      return Object.entries(extraFilter).every(([k, v]) => data[k] === v);
    });

    if (matching.length === 0) return;

    const batches = [];
    for (let i = 0; i < matching.length; i += 500) {
      const batch = writeBatch(db);
      matching.slice(i, i + 500).forEach((d) => {
        batch.update(doc(db, 'notifications', d.id), { read: true, readAt: serverTimestamp() });
      });
      batches.push(batch.commit());
    }
    await Promise.all(batches);
  } catch (error) {
    console.error(`Error marking ${bucket} notifications as read:`, error);
  }
}

/**
 * Mark unread notifications for a landlord as read.
 * Only queries recipientUserId — the canonical field for notification recipients.
 */
export async function markAllLandlordNotificationsAsRead(userId) {
  if (!userId) return;
  try {
    const q = query(collection(db, 'notifications'), where('recipientUserId', '==', userId), where('read', '==', false));
    const snap = await getDocs(q).catch(() => ({ docs: [] }));
    if (snap.docs.length === 0) return;
    const batches = [];
    for (let i = 0; i < snap.docs.length; i += 500) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + 500).forEach((d) => {
        batch.update(doc(db, 'notifications', d.id), { read: true, readAt: serverTimestamp() });
      });
      batches.push(batch.commit());
    }
    await Promise.all(batches);
  } catch (error) {
    console.error('Error marking landlord notifications as read:', error);
  }
}

export async function getUnreadNotifications(userId, maxResults = 50) {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientUserId', '==', userId),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
    const snapshot = await getDocs(notificationsQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting unread notifications:', error);
    throw error;
  }
}

